import StylesPanel from './StylesPanel.jsx';
import SaveThemeModal from './SaveThemeModal.jsx';
import MakeThemePublicModal from './MakeThemePublicModal.jsx';
import {
    invalidateAllFormDetailsQueries,
    invalidateThemesQuery,
    setFormDetailsQuery,
    useFormDetailsQuery,
    useThemesQuery,
} from '../../lib/Queries';
import { useCallback, useEffect, useRef, useState } from 'react';
import useBusinessPostApi from '../../lib/useBusinessPostApi.js';
import { businessApiRoute, post } from '../../lib/Api';
import { useDebounce } from '@uidotdev/usehooks';
import * as Toast from '../../lib/Toast.js';

const FormStylesPanel = ({ isOpen, onClose, formId }) => {
    const { data } = useFormDetailsQuery(formId);
    const { save } = useBusinessPostApi(`/forms/${formId}`);
    const { data: themesData, isLoading: themesLoading } =
        useThemesQuery(isOpen);
    const [styles, setStyles] = useState(data?.form?.settings?.styles || {});
    const debouncedStyles = useDebounce(styles, 500);

    const [isSavingTheme, setIsSavingTheme] = useState(false);
    const [isNamingTheme, setIsNamingTheme] = useState(false);
    // The theme being confirmed for publishing, from the theme list. Kept apart
    //  from whether the modal is open so the theme's name stays put while the
    //  modal fades out
    const [isPublishingTheme, setIsPublishingTheme] = useState(false);
    const [publishingTheme, setPublishingTheme] = useState(null);
    // Likewise for the theme whose name and description are being edited
    const [isEditingTheme, setIsEditingTheme] = useState(false);
    const [editingTheme, setEditingTheme] = useState(null);
    // Whether anything has been changed since the panel opened, and whether the
    //  offer to save it has been waved away
    const [hasEdited, setHasEdited] = useState(false);
    const [hasDismissed, setHasDismissed] = useState(false);

    // Carries its own settings, so the panel can show the theme without
    //  waiting on the themes list to load
    const theme = data?.theme || null;

    // Until something is changed here the panel simply follows the server, and
    //  from the first edit onwards it stops listening.
    //
    //  Both halves matter. Following the server means a payload that arrives
    //  late, or was stale in the cache, still lands — a theme this form uses
    //  may have been edited from a different form since, and seeding from the
    //  old copy would show the wrong styling. Going deaf after the first edit
    //  is what stops the save response, which carries the styles as they were
    //  when the request went out, from throwing away whatever was typed during
    //  the round trip — that lost the last characters of anything in progress.
    const hasEditedRef = useRef(false);

    useEffect(() => {
        if (!isOpen) {
            // Opening again picks up whatever changed while it was shut
            hasEditedRef.current = false;
            setHasEdited(false);
            setHasDismissed(false);

            return;
        }

        if (!data || hasEditedRef.current) {
            return;
        }

        // Already resolved against the theme, so the panel shows what the form
        //  actually looks like. What differs from the theme is what the server
        //  keeps as this form's own
        setStyles(data?.form?.settings?.styles || {});
    }, [isOpen, formId, data]);

    const editStyles = useCallback(newStyles => {
        hasEditedRef.current = true;
        setHasEdited(true);
        setStyles(newStyles);
    }, []);

    useEffect(() => {
        if (!data || !hasEditedRef.current) {
            // Nothing has been changed here, so there is nothing to save.
            //  Posting anyway would write whatever the panel happens to be
            //  holding back onto the form — and against a theme that has moved
            //  on since, those stale values are saved as deliberate overrides
            return;
        }

        setFormDetailsQuery(formId, {
            ...data,
            form: {
                ...data?.form,
                settings: {
                    ...data?.form?.settings,
                    styles: styles,
                },
            },
        });

        // Posted whole. The server keeps only what differs from the theme, so
        //  echoing the theme's own values back doesn't mark the form customized
        save({
            styles: styles,
        }).then(results => {
            setFormDetailsQuery(formId, results);
        });
    }, [JSON.stringify(debouncedStyles)]);

    function afterThemeChange() {
        invalidateThemesQuery();
        // Every form, not just this one: pushing changes into a theme restyles
        //  each form using it, so their cached payloads are now out of date
        invalidateAllFormDetailsQueries();
        // The form's own styles changed server-side, so the panel goes back to
        //  following the server rather than keeping what it had
        hasEditedRef.current = false;
        setHasEdited(false);
        setHasDismissed(false);
    }

    function themeRequest(route, body, message) {
        setIsSavingTheme(true);

        return post(businessApiRoute(route), body)
            .then(() => {
                if (message) {
                    Toast.success(message);
                }
                afterThemeChange();
            })
            .catch(err => {
                Toast.error(
                    err.response?.data?.message ||
                        'Something went wrong, please try again',
                );
            })
            .finally(() => {
                setIsSavingTheme(false);
            });
    }

    const themeActions = {
        onApply: themeId =>
            themeRequest(
                `/forms/${formId}/theme`,
                { theme_id: themeId },
                'Theme applied',
            ),
        onDetach: () =>
            themeRequest(
                `/forms/${formId}/theme`,
                { theme_id: null },
                'This form now has its own styles',
            ),
        // Every form on the theme is detached first and keeps the look it has,
        //  so this changes the list rather than any form's appearance
        onDelete: themeId =>
            themeRequest(`/themes/${themeId}/delete`, {}, 'Theme deleted'),
        // Only comes out of the listing — forms already on it keep it
        onUnpublish: unpublishTheme =>
            themeRequest(
                `/themes/${unpublishTheme.id}/unpublish`,
                {},
                `${unpublishTheme.name} is no longer public`,
            ),
        onEdit: editTheme => {
            setEditingTheme(editTheme);
            setIsEditingTheme(true);
        },
    };

    // A form overrides the theme it uses, so it can sit there customized
    //  indefinitely — that is worth offering to reconcile every time the panel
    //  is opened. Without a theme there is nothing to diverge from, so the
    //  offer only follows an actual edit
    const isCustomized = theme ? theme.isCustomized || hasEdited : hasEdited;

    return (
        <>
            <StylesPanel
                isOpen={isOpen}
                onClose={onClose}
                styles={styles}
                setStyles={editStyles}
                objectId={formId}
                objectType="form"
                title="Form Styles"
                customCssWarnings={data?.customCssWarnings}
                customCssEnabled={data?.customCssEnabled}
                showThemes={true}
                theme={theme}
                themes={themesData?.themes || []}
                themesLoading={themesLoading}
                themeActions={themeActions}
                isSavingTheme={isSavingTheme}
                showThemeChanges={isCustomized && !hasDismissed}
                onSaveAsNewTheme={() => setIsNamingTheme(true)}
                onUpdateTheme={() =>
                    themeRequest(
                        `/forms/${formId}/theme/save`,
                        {},
                        `${theme?.name} updated everywhere it is used`,
                    )
                }
                onDismissThemeChanges={() => setHasDismissed(true)}
                onPublishTheme={publishTheme => {
                    setPublishingTheme(publishTheme);
                    setIsPublishingTheme(true);
                }}
            />

            <SaveThemeModal
                isOpen={isNamingTheme}
                isSaving={isSavingTheme}
                onClose={() => setIsNamingTheme(false)}
                onSave={({ name, author, description }) =>
                    themeRequest(
                        '/themes',
                        { name, author, description, form_id: formId },
                        'Theme saved',
                    ).then(() => setIsNamingTheme(false))
                }
            />

            <SaveThemeModal
                isOpen={isEditingTheme}
                isSaving={isSavingTheme}
                theme={editingTheme}
                onClose={() => setIsEditingTheme(false)}
                onSave={({ name, author, description }) =>
                    themeRequest(
                        `/themes/${editingTheme?.id}`,
                        { name, author, description },
                        'Theme updated',
                    ).then(() => setIsEditingTheme(false))
                }
            />

            <MakeThemePublicModal
                isOpen={isPublishingTheme}
                isSaving={isSavingTheme}
                theme={publishingTheme}
                onClose={() => setIsPublishingTheme(false)}
                onConfirm={() =>
                    themeRequest(
                        `/themes/${publishingTheme?.id}/publish`,
                        {},
                        `${publishingTheme?.name} is now public`,
                    ).then(() => setIsPublishingTheme(false))
                }
            />
        </>
    );
};

export default FormStylesPanel;
